/*
 * MSTD cart drawer.
 * - Shopify is the single source of truth: every update re-renders the drawer from the
 *   section's Liquid (Section Rendering API). No client-side cart, prices or discounts.
 * - All mutations go through one queue, so rapid taps can't race each other.
 * - Uses only document-level delegation + data-mstd-* hooks; touches nothing else in the theme.
 * Public API: window.MstdCart.open() / .close() / .refresh()
 * Events: dispatch 'mstd:cart:open' or 'mstd:cart:refresh' on document;
 *         listens for nothing else. Emits 'mstd:cart:updated' { detail: { itemCount } }.
 */
(() => {
  if (window.MstdCart) return;

  const ROOT_SEL = '[data-mstd]';
  const CART_URL_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const QTY_DEBOUNCE = 400;
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  const nativeFetch = window.fetch.bind(window);
  const routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const getRoot = () => document.querySelector(ROOT_SEL);
  const cfg = () => {
    const d = (getRoot() || {}).dataset || {};
    return {
      sectionId: d.sectionId || 'mstd-cart-drawer-v4',
      openOnAdd: d.openOnAdd === 'true',
      observe: d.observeCart === 'true',
      interceptLinks: d.interceptLinks === 'true',
      linkSelector: d.linkSelector || '',
      countSelector: d.countSelector || '',
      dotSelector: d.dotSelector || '',
      dotClass: d.dotClass || '',
      ajaxAdd: d.ajaxAdd === 'true',
    };
  };

  let queue = Promise.resolve();
  let lastFocus = null;
  let sheetReturnFocus = null;
  let openSheetId = null;
  let errorTimer = null;
  let externalTimer = null;
  const pendingQty = new Map(); // line key -> { timer, qty }

  /* ---------- helpers ---------- */

  const enqueue = (task) => {
    const run = queue.then(task, task);
    queue = run.catch(() => {});
    return run;
  };

  const isOpen = () => !!getRoot()?.classList.contains('is-open');

  function announce(msg) {
    const live = getRoot()?.querySelector('[data-mstd-live]');
    if (!live) return;
    live.textContent = '';
    requestAnimationFrame(() => (live.textContent = msg));
  }

  function showError(msg) {
    const el = getRoot()?.querySelector('[data-mstd-error]');
    if (!el) return;
    el.textContent = msg; // textContent only — never inject HTML
    el.hidden = false;
    clearTimeout(errorTimer);
    errorTimer = setTimeout(() => (el.hidden = true), 5000);
  }

  function setBusy(busy, key) {
    const root = getRoot();
    if (!root) return;
    root.classList.toggle('is-busy', busy);
    if (key) {
      root.querySelectorAll('[data-mstd-line]').forEach((li) => {
        if (li.dataset.mstdLine === key) li.classList.toggle('is-updating', busy);
      });
    }
  }

  /* ---------- rendering from Shopify ---------- */

  async function fetchSection() {
    const { sectionId } = cfg();
    const res = await nativeFetch(`${routesRoot}?sections=${encodeURIComponent(sectionId)}`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`Cart refresh failed (${res.status})`);
    const data = await res.json();
    return data[sectionId];
  }

  function applyHtml(html) {
    const root = getRoot();
    if (!root || typeof html !== 'string') return;
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const scroll = root.querySelector('[data-mstd-scroll]');
    const scrollTop = scroll ? scroll.scrollTop : 0;
    const track = root.querySelector('[data-mstd-offers-track]');
    const trackLeft = track ? track.scrollLeft : 0;
    const focusId = document.activeElement?.closest?.('[data-mstd-focus]')?.dataset.mstdFocus;

    root.querySelectorAll('[data-mstd-region]').forEach((region) => {
      const fresh = doc.querySelector(`[data-mstd-region="${region.dataset.mstdRegion}"]`);
      if (fresh) region.replaceWith(document.importNode(fresh, true));
    });

    // Keep in-flight quantity edits visible after a re-render.
    pendingQty.forEach((p, key) => {
      root.querySelectorAll('.mstd-qty__input').forEach((input) => {
        if (input.dataset.key === key) input.value = p.qty;
      });
    });

    const newScroll = root.querySelector('[data-mstd-scroll]');
    if (newScroll) newScroll.scrollTop = scrollTop;
    const newTrack = root.querySelector('[data-mstd-offers-track]');
    if (newTrack) newTrack.scrollLeft = trackLeft;

    if (openSheetId) {
      const sheet = findSheet(openSheetId);
      if (sheet) {
        sheet.hidden = false;
        sheet.classList.add('is-open');
        sheet.querySelector('.mstd-sheet__panel')?.focus({ preventScroll: true });
      } else {
        openSheetId = null;
      }
    } else if (focusId && isOpen()) {
      const target = [...root.querySelectorAll('[data-mstd-focus]')].find(
        (el) => el.dataset.mstdFocus === focusId
      );
      (target || root.querySelector('.mstd__panel'))?.focus({ preventScroll: true });
    }

    syncCount();
    updateOfferNav();
    syncGifts();
  }

  /* ---------- offer slider arrows ---------- */

  function updateOfferNav() {
    const slider = getRoot()?.querySelector('[data-mstd-offers-slider]');
    const track = slider?.querySelector('[data-mstd-offers-track]');
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    slider.classList.toggle('has-overflow', max > 4);
    const prev = slider.querySelector('[data-mstd-offers-nav="-1"]');
    const next = slider.querySelector('[data-mstd-offers-nav="1"]');
    if (prev) prev.disabled = track.scrollLeft <= 4;
    if (next) next.disabled = track.scrollLeft >= max - 4;
  }

  function slideOffers(dir) {
    const track = getRoot()?.querySelector('[data-mstd-offers-track]');
    if (!track) return;
    const card = track.querySelector('.mstd-offer');
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: dir * step, behavior: reduce ? 'auto' : 'smooth' });
    // Don't depend only on scroll events (some browsers throttle them) — re-check after the animation.
    updateOfferNav();
    setTimeout(updateOfferNav, 450);
  }

  function syncCount() {
    const main = getRoot()?.querySelector('[data-mstd-region="main"]');
    if (!main) return;
    const itemCount = parseInt(main.dataset.itemCount, 10) || 0;
    const { countSelector, dotSelector, dotClass } = cfg();
    try {
      if (countSelector) {
        document.querySelectorAll(countSelector).forEach((el) => (el.textContent = String(itemCount)));
      }
      // Themes that show a dot instead of a number (Impulse: .cart-link__bubble--visible).
      if (dotSelector && dotClass) {
        document.querySelectorAll(dotSelector).forEach((el) => el.classList.toggle(dotClass, itemCount > 0));
      }
    } catch (e) {
      /* invalid selector in settings — ignore */
    }
    document.dispatchEvent(new CustomEvent('mstd:cart:updated', { detail: { itemCount } }));
  }

  function refresh() {
    return enqueue(async () => {
      setBusy(true);
      try {
        applyHtml(await fetchSection());
      } catch (e) {
        showError('Could not refresh your cart. Please try again.');
      } finally {
        setBusy(false);
      }
    });
  }

  function changeLine(key, quantity) {
    return enqueue(async () => {
      setBusy(true, key);
      const { sectionId } = cfg();
      try {
        const res = await nativeFetch(`${routesRoot}cart/change.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: key,
            quantity,
            sections: [sectionId],
            sections_url: window.location.pathname,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          showError(data.description || data.message || 'That quantity is not available.');
          applyHtml(await fetchSection());
          return;
        }
        if (data.sections && data.sections[sectionId]) applyHtml(data.sections[sectionId]);
        else applyHtml(await fetchSection());
        announce(quantity === 0 ? 'Item removed from cart.' : 'Cart updated.');
      } catch (e) {
        showError('Network error — your cart was not changed.');
      } finally {
        setBusy(false, key);
      }
    });
  }

  function scheduleQty(key, qty) {
    qty = Math.max(0, qty);
    const prev = pendingQty.get(key);
    if (prev) clearTimeout(prev.timer);
    const timer = setTimeout(() => {
      pendingQty.delete(key);
      changeLine(key, qty);
    }, QTY_DEBOUNCE);
    pendingQty.set(key, { timer, qty });
  }

  /* ---------- free gift (cart value offer with reward "Free gift product") ---------- */
  // The section renders one [data-mstd-gift] marker per gift offer with Shopify's view of the cart.
  // - cart qualifies, gift not in cart  -> add 1 gift line (property _mstd_gift = offer id)
  // - cart no longer qualifies          -> remove the gift line
  // - gift line is NOT ₹0               -> Shopify's Buy X Get Y discount is missing/not matching:
  //                                        remove it so nobody pays for an automatic add
  // The customer removing the gift ("declined") is respected for the rest of the session.
  const giftStore = {
    get(id) {
      try {
        return sessionStorage.getItem('mstd-gift-' + id);
      } catch (e) {
        return null;
      }
    },
    set(id, value) {
      try {
        if (value == null) sessionStorage.removeItem('mstd-gift-' + id);
        else sessionStorage.setItem('mstd-gift-' + id, value);
      } catch (e) {
        /* storage unavailable — gift still works, just not remembered */
      }
    },
  };
  let giftBusy = false;
  const onCartPage = () => /\/cart\/?$/.test(window.location.pathname);

  function giftDone(changed) {
    giftBusy = false;
    // The theme's own /cart page is server-rendered; reload it so it shows the gift change.
    if (changed && onCartPage()) window.location.reload();
  }

  function addGift(id, variant) {
    giftBusy = true;
    return enqueue(async () => {
      const { sectionId } = cfg();
      let html = null;
      try {
        const res = await nativeFetch(`${routesRoot}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(variant), quantity: 1, properties: { _mstd_gift: id } }],
            sections: [sectionId],
            sections_url: window.location.pathname,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          giftStore.set(id, 'blocked'); // e.g. sold out — don't retry this session
          giftDone(false);
          return;
        }
        html = data.sections && data.sections[sectionId];
        if (!html) html = await fetchSection();
        announce('Free gift added to your cart.');
      } catch (e) {
        giftStore.set(id, 'blocked');
        giftDone(false);
        return;
      }
      giftBusy = false; // let syncGifts verify the result (is it really ₹0?)
      applyHtml(html);
      if (onCartPage()) window.location.reload();
    });
  }

  function removeGift(key) {
    giftBusy = true;
    changeLine(key, 0).finally(() => giftDone(true));
  }

  function giftAllowed(id, marker) {
    const memory = giftStore.get(id);
    if (!memory) return true;
    if (memory.startsWith('blocked:')) {
      return (parseInt(marker.dataset.measure, 10) || 0) > parseInt(memory.slice(8), 10);
    }
    return false; // 'declined' by the customer, or 'blocked' (could not be added, e.g. sold out)
  }

  function syncGifts() {
    if (giftBusy) return;
    const root = getRoot();
    if (!root) return;
    for (const m of root.querySelectorAll('[data-mstd-gift]')) {
      const id = m.dataset.mstdGift;
      const key = m.dataset.lineKey;
      const eligible = m.dataset.eligible === 'true';
      if (key) {
        if (!eligible) return removeGift(key);
        if (m.dataset.lineFree !== 'true') {
          // Remember the cart value Shopify refused at; try again only when the cart grows.
          giftStore.set(id, 'blocked:' + (parseInt(m.dataset.measure, 10) || 0));
          console.warn(
            'MSTD cart drawer: free gift removed because Shopify did not make it ₹0. ' +
              'Check the Buy X Get Y automatic discount for this gift.'
          );
          return removeGift(key);
        }
        if ((parseInt(m.dataset.lineQty, 10) || 1) > 1) {
          giftBusy = true;
          changeLine(key, 1).finally(() => giftDone(true));
          return;
        }
      } else if (eligible && m.dataset.available === 'true' && m.dataset.variant && giftAllowed(id, m)) {
        addGift(id, m.dataset.variant);
        return;
      }
    }
  }

  /* ---------- open / close ---------- */

  function open() {
    const root = getRoot();
    if (!root) return;
    if (!isOpen()) {
      lastFocus = document.activeElement;
      root.removeAttribute('inert');
      root.setAttribute('aria-hidden', 'false');
      root.classList.add('is-open');
      document.documentElement.classList.add('mstd-lock');
      requestAnimationFrame(() =>
        (root.querySelector('[data-mstd-focus="close"]') || root.querySelector('.mstd__panel'))?.focus({
          preventScroll: true,
        })
      );
    }
    refresh();
  }

  function close() {
    const root = getRoot();
    if (!root || !isOpen()) return;
    closeSheet(false);
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    root.setAttribute('inert', '');
    document.documentElement.classList.remove('mstd-lock');
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
  }

  function findSheet(id) {
    const root = getRoot();
    if (!root) return null;
    return [...root.querySelectorAll('[data-mstd-sheet]')].find((s) => s.dataset.mstdSheet === id) || null;
  }

  function openSheet(id, trigger) {
    const sheet = findSheet(id);
    if (!sheet) return;
    openSheetId = id;
    sheetReturnFocus = trigger ? trigger.dataset.mstdFocus : null;
    sheet.hidden = false;
    requestAnimationFrame(() => {
      sheet.classList.add('is-open');
      sheet.querySelector('.mstd-sheet__panel')?.focus({ preventScroll: true });
    });
  }

  function closeSheet(restoreFocus = true) {
    if (!openSheetId) return;
    const sheet = findSheet(openSheetId);
    openSheetId = null;
    if (sheet) {
      sheet.classList.remove('is-open');
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      setTimeout(() => {
        if (!sheet.classList.contains('is-open')) sheet.hidden = true;
      }, reduce ? 0 : 300);
    }
    if (restoreFocus && sheetReturnFocus) {
      const root = getRoot();
      const t = root && [...root.querySelectorAll('[data-mstd-focus]')].find((el) => el.dataset.mstdFocus === sheetReturnFocus);
      t?.focus({ preventScroll: true });
    }
  }

  function trapFocus(e) {
    const root = getRoot();
    const container = openSheetId
      ? findSheet(openSheetId)?.querySelector('.mstd-sheet__panel')
      : root?.querySelector('.mstd__panel');
    if (!container) return;
    const items = [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) {
      e.preventDefault();
      container.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === container)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ---------- events (delegated, registered once) ---------- */

  document.addEventListener('click', (e) => {
    const root = getRoot();
    if (!root) return;
    const t = e.target;

    // Cart icon → open drawer (capture phase listener below handles this; here only drawer clicks)
    if (!root.contains(t)) return;

    const nav = t.closest('[data-mstd-offers-nav]');
    if (nav) return slideOffers(parseInt(nav.dataset.mstdOffersNav, 10));
    if (t.closest('[data-mstd-sheet-close]')) return closeSheet();
    const sheetBtn = t.closest('[data-mstd-sheet-open]');
    if (sheetBtn) return openSheet(sheetBtn.dataset.mstdSheetOpen, sheetBtn);
    if (t.closest('[data-mstd-close]')) return close();

    const step = t.closest('[data-mstd-step]');
    if (step) {
      const key = step.dataset.key;
      const input = step.closest('.mstd-qty')?.querySelector('.mstd-qty__input');
      const current = pendingQty.has(key) ? pendingQty.get(key).qty : parseInt(input?.value, 10) || 0;
      let next = current + parseInt(step.dataset.mstdStep, 10);
      const max = input && input.max ? parseInt(input.max, 10) : Infinity;
      next = Math.max(0, Math.min(next, max));
      if (input) input.value = next;
      scheduleQty(key, next);
      return;
    }

    const giftAdd = t.closest('[data-mstd-gift-add]');
    if (giftAdd) {
      giftStore.set(giftAdd.dataset.mstdGiftAdd, null); // customer asked for it again
      closeSheet(false);
      addGift(giftAdd.dataset.mstdGiftAdd, giftAdd.dataset.variant);
      return;
    }

    const remove = t.closest('[data-mstd-remove]');
    if (remove) {
      if (remove.dataset.mstdGiftLine) giftStore.set(remove.dataset.mstdGiftLine, 'declined');
      const key = remove.dataset.key;
      const p = pendingQty.get(key);
      if (p) clearTimeout(p.timer);
      pendingQty.delete(key);
      changeLine(key, 0);
    }
  });

  document.addEventListener('change', (e) => {
    const input = e.target.closest?.(`${ROOT_SEL} .mstd-qty__input`);
    if (!input) return;
    const qty = parseInt(input.value, 10);
    if (Number.isNaN(qty) || qty < 0) {
      input.value = input.defaultValue;
      return;
    }
    scheduleQty(input.dataset.key, qty);
  });

  document.addEventListener('keydown', (e) => {
    if (!isOpen()) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      if (openSheetId) closeSheet();
      else close();
    } else if (e.key === 'Tab') {
      trapFocus(e);
    }
  });

  // Cart icon clicks — capture phase so the theme's own cart drawer does not also open.
  document.addEventListener(
    'click',
    (e) => {
      const c = cfg();
      if (!c.interceptLinks || !c.linkSelector) return;
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      let link;
      try {
        link = e.target.closest(c.linkSelector);
      } catch (err) {
        return; // invalid selector in settings
      }
      const root = getRoot();
      if (!link || !root || root.contains(link)) return;
      if (/\/cart\/?$/.test(window.location.pathname)) return; // already on cart page
      e.preventDefault();
      e.stopPropagation();
      open();
    },
    true
  );

  // Keep arrow states in sync with swipe/scroll (scroll doesn't bubble → capture) and resizes.
  const onTrackScroll = (e) => {
    if (e.target instanceof Element && e.target.matches('[data-mstd-offers-track]')) updateOfferNav();
  };
  document.addEventListener('scroll', onTrackScroll, true);
  document.addEventListener('scrollend', onTrackScroll, true);
  window.addEventListener('resize', updateOfferNav);
  updateOfferNav();
  syncGifts(); // page may load with a cart that already qualifies (or no longer does)

  /* ---------- instant add to cart (themes whose cart type is "Page", e.g. Impulse) ---------- */
  // Takes over only plain product forms posting to /cart/add that the theme has NOT already
  // handled (defaultPrevented). On a network failure it falls back to the normal form submit.
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || e.defaultPrevented) return;
    if (!/\/cart\/add(\.js)?(\?|$)/.test(form.getAttribute('action') || '')) return;
    if (!cfg().ajaxAdd || !getRoot()) return;
    e.preventDefault();

    const btn = e.submitter || form.querySelector('[type="submit"]');
    if (btn && btn.getAttribute('aria-busy') === 'true') return; // double tap
    if (btn) btn.setAttribute('aria-busy', 'true');
    const body = new FormData(form);

    nativeFetch(`${routesRoot}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        open(); // re-renders from Shopify in every case
        if (!res.ok) showError(data.description || data.message || 'This item could not be added.');
        else announce('Added to cart.');
        document.dispatchEvent(new CustomEvent('mstd:cart:added', { detail: { ok: res.ok, response: data } }));
      })
      .catch(() => HTMLFormElement.prototype.submit.call(form))
      .finally(() => {
        if (btn) btn.removeAttribute('aria-busy');
      });
  });

  document.addEventListener('mstd:cart:open', open);
  document.addEventListener('mstd:cart:refresh', () => refresh());

  // Back from checkout (bfcache) — page is restored without re-rendering Liquid.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) refresh();
  });

  // Theme editor support.
  document.addEventListener('shopify:section:select', (e) => {
    if (e.detail && e.detail.sectionId === cfg().sectionId) open();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    if (e.detail && e.detail.sectionId === cfg().sectionId) close();
  });
  document.addEventListener('shopify:block:select', (e) => {
    if (e.detail && e.detail.sectionId === cfg().sectionId) open();
  });

  /* ---------- observe the theme's own cart requests (read-only) ---------- */

  function onExternalCartChange(isAdd) {
    clearTimeout(externalTimer);
    externalTimer = setTimeout(() => {
      if (isAdd && cfg().openOnAdd) open(); // open() also refreshes
      else refresh();
    }, 60);
  }

  const urlOf = (input) => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    return (input && input.url) || '';
  };

  if (cfg().observe) {
    window.fetch = function (input, init) {
      const promise = nativeFetch(input, init);
      const url = urlOf(input);
      if (CART_URL_RE.test(url)) {
        promise
          .then((res) => {
            if (res.ok) onExternalCartChange(/\/cart\/add/.test(url));
          })
          .catch(() => {});
      }
      return promise;
    };

    const xhrOpen = XMLHttpRequest.prototype.open;
    const xhrSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__mstdUrl = String(url || '');
      return xhrOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      const url = this.__mstdUrl || '';
      if (CART_URL_RE.test(url)) {
        this.addEventListener('load', () => {
          if (this.status >= 200 && this.status < 300) onExternalCartChange(/\/cart\/add/.test(url));
        });
      }
      return xhrSend.apply(this, arguments);
    };
  }

  window.MstdCart = { open, close, refresh };
})();
